import { BuilderSelectType } from '@/calendesk/models/BuilderSelectType'

// Mirrors the renderer's consent module: the values below are stored in
// draft.configuration and read by the client website as they are.
export enum GoogleTagMode {
  IMMEDIATELY = 'immediately',
  AFTER_CONSENT = 'after_consent'
}

export enum ConsentCategory {
  FUNCTIONALITY = 'functionality',
  ANALYTICS = 'analytics',
  MARKETING = 'marketing'
}

export const CUSTOM_CODE_ALWAYS = 'always'

// Per-element value of `data-category` on a script or link: the element loads
// right away even when the whole custom-code field waits for a consent.
export const CUSTOM_CODE_NECESSARY = 'necessary'

// Where the banner's privacy-policy link points. One choice, never two fields
// competing with each other.
export enum PrivacyPolicyTarget {
  NONE = 'none',
  PAGE = 'page',
  URL = 'url'
}

export const PRIVACY_POLICY_TARGET_KEY = 'wb_cookie_banner_privacy_policy_target__select__'

// How a visitor gets back to the cookie settings after answering the banner.
export enum CookieReopen {
  ICON = 'icon',
  LINK = 'link',
  BOTH = 'both'
}

export const COOKIE_REOPEN_KEY = 'wb_cookie_banner_reopen__select__'

// Any link on the site pointing here opens the cookie settings; the renderer
// intercepts it. The tenant pastes it as the address of a menu or footer entry.
export const COOKIE_SETTINGS_HASH = '#cookie-settings'

// Ids 1-4 belong to the login, signup and forgotten-password configurations.
export const COOKIE_BANNER_CONFIGURATION_ID = 5

export const COOKIE_BANNER_ENABLED_KEY = 'wb_cookie_banner_enabled__checkbox__'
export const COOKIE_BANNER_REVISION_KEY = 'cookie_banner_revision'
export const GOOGLE_TAG_MODE_KEY = 'wb_google_tag_mode__select__'
export const CUSTOM_CODE_MODE_KEY = 'wb_custom_code_mode__select__'
export const COOKIE_BANNER_CONFIGURATION_KEY = 'cookie_banner_configuration'

export const OPTIONAL_CATEGORIES: ReadonlyArray<ConsentCategory> = [
  ConsentCategory.FUNCTIONALITY,
  ConsentCategory.ANALYTICS,
  ConsentCategory.MARKETING
]

export function categoryVisibilityKey (category: ConsentCategory): string {
  return `wb_cookie_banner_show_${category}__checkbox__`
}

// The optional categories the visitor gets to choose from. A hidden category is
// never accepted, so scripts assigned to it never run on that site.
export function visibleCategories (bannerConfiguration: Record<string, any> | null | undefined): ConsentCategory[] {
  return OPTIONAL_CATEGORIES.filter(category => bannerConfiguration?.[categoryVisibilityKey(category)] !== false)
}

// Every text the tenant can edit, in the order the editor shows them. The
// stem doubles as the translation key of its label and, with the
// `wb_cookie_banner_default_` prefix, of its default text.
export const COOKIE_BANNER_TEXT_KEYS: ReadonlyArray<string> = [
  'wb_cookie_banner_title__text__',
  'wb_cookie_banner_description__html_text__',
  'wb_cookie_banner_accept_all_button__text__',
  'wb_cookie_banner_reject_all_button__text__',
  'wb_cookie_banner_settings_button__text__',
  'wb_cookie_banner_privacy_policy_link__text__',
  'wb_cookie_banner_preferences_title__text__',
  'wb_cookie_banner_preferences_description__html_text__',
  'wb_cookie_banner_save_button__text__',
  'wb_cookie_banner_necessary_title__text__',
  'wb_cookie_banner_necessary_description__html_text__',
  'wb_cookie_banner_functionality_title__text__',
  'wb_cookie_banner_functionality_description__html_text__',
  'wb_cookie_banner_analytics_title__text__',
  'wb_cookie_banner_analytics_description__html_text__',
  'wb_cookie_banner_marketing_title__text__',
  'wb_cookie_banner_marketing_description__html_text__'
]

function stem (key: string): string {
  return key.replace(/__[a-z_0-9]+?__$/, '')
}

export function defaultTextTranslationKey (key: string): string {
  return stem(key).replace('wb_cookie_banner_', 'wb_cookie_banner_default_')
}

export function cookieBannerConfigurationDefaults (): Record<string, any> {
  const configuration: Record<string, any> = { id: COOKIE_BANNER_CONFIGURATION_ID }

  configuration.wb_cookie_banner_title__text__ = null
  configuration.wb_cookie_banner_description__html_text__ = null
  configuration.wb_cookie_banner_accept_all_button__text__ = null
  configuration.wb_cookie_banner_reject_all_button__text__ = null
  configuration.wb_cookie_banner_settings_button__text__ = null
  configuration[PRIVACY_POLICY_TARGET_KEY] = { type: BuilderSelectType.PRIVACY_POLICY_TARGET, value: PrivacyPolicyTarget.NONE }
  configuration.wb_cookie_banner_privacy_policy_page__page_id__ = null
  configuration.wb_cookie_banner_privacy_policy_url__url__ = null
  configuration.wb_cookie_banner_privacy_policy_link__text__ = null
  configuration.wb_cookie_banner_block_page__checkbox__ = false
  configuration[COOKIE_REOPEN_KEY] = { type: BuilderSelectType.COOKIE_REOPEN, value: CookieReopen.ICON }
  configuration.wb_cookie_banner_preferences_title__text__ = null
  configuration.wb_cookie_banner_preferences_description__html_text__ = null
  configuration.wb_cookie_banner_save_button__text__ = null
  configuration.wb_cookie_banner_show_functionality__checkbox__ = true
  configuration.wb_cookie_banner_show_analytics__checkbox__ = true
  configuration.wb_cookie_banner_show_marketing__checkbox__ = true
  configuration.wb_cookie_banner_necessary_title__text__ = null
  configuration.wb_cookie_banner_necessary_description__html_text__ = null
  configuration.wb_cookie_banner_functionality_title__text__ = null
  configuration.wb_cookie_banner_functionality_description__html_text__ = null
  configuration.wb_cookie_banner_analytics_title__text__ = null
  configuration.wb_cookie_banner_analytics_description__html_text__ = null
  configuration.wb_cookie_banner_marketing_title__text__ = null
  configuration.wb_cookie_banner_marketing_description__html_text__ = null

  return configuration
}

export function cookieBannerTopLevelDefaults (): Record<string, any> {
  return {
    [COOKIE_BANNER_ENABLED_KEY]: false,
    [COOKIE_BANNER_REVISION_KEY]: 0,
    [GOOGLE_TAG_MODE_KEY]: { type: BuilderSelectType.GOOGLE_TAG_MODE, value: GoogleTagMode.IMMEDIATELY },
    [CUSTOM_CODE_MODE_KEY]: { type: BuilderSelectType.CONSENT_CATEGORY, value: CUSTOM_CODE_ALWAYS }
  }
}

// Back-fill for drafts created before the banner existed. Adds only what is
// missing: a site that never touched the banner keeps today's behaviour
// (banner off, Google Tag Manager and custom code loading right away).
export function addMissingCookieBannerKeys (configuration: Record<string, any>): void {
  const topLevel = cookieBannerTopLevelDefaults()

  for (const key in topLevel) {
    if (typeof configuration[key] === 'undefined') {
      configuration[key] = topLevel[key]
    }
  }

  if (!configuration[COOKIE_BANNER_CONFIGURATION_KEY]) {
    configuration[COOKIE_BANNER_CONFIGURATION_KEY] = cookieBannerConfigurationDefaults()
    return
  }

  const nested = cookieBannerConfigurationDefaults()

  for (const key in nested) {
    if (typeof configuration[COOKIE_BANNER_CONFIGURATION_KEY][key] === 'undefined') {
      configuration[COOKIE_BANNER_CONFIGURATION_KEY][key] = nested[key]
    }
  }
}

export interface CookieBannerPrefill {
  texts: Record<string, string>;
  topLevel: Record<string, any>;
}

// What switching the banner on fills in: every empty text gets its default in
// the builder's language, and a Google Tag Manager id or custom code that is
// still on today's "load right away" setting moves to the safe mode. Nothing
// the tenant already set is overwritten.
export function cookieBannerPrefill (configuration: Record<string, any>, translate: (key: string) => string): CookieBannerPrefill {
  const current = configuration[COOKIE_BANNER_CONFIGURATION_KEY] || {}
  const texts: Record<string, string> = {}

  COOKIE_BANNER_TEXT_KEYS.forEach(key => {
    const value = current[key]

    if (typeof value !== 'string' || value.trim() === '') {
      texts[key] = translate(defaultTextTranslationKey(key))
    }
  })

  const topLevel: Record<string, any> = {}

  if (configuration.wb_google_tag_id__text__ && configuration[GOOGLE_TAG_MODE_KEY]?.value === GoogleTagMode.IMMEDIATELY) {
    topLevel[GOOGLE_TAG_MODE_KEY] = { type: BuilderSelectType.GOOGLE_TAG_MODE, value: GoogleTagMode.AFTER_CONSENT }
  }

  if (configuration.wb_custom_code__long_text__ && configuration[CUSTOM_CODE_MODE_KEY]?.value === CUSTOM_CODE_ALWAYS) {
    topLevel[CUSTOM_CODE_MODE_KEY] = { type: BuilderSelectType.CONSENT_CATEGORY, value: ConsentCategory.MARKETING }
  }

  return { texts, topLevel }
}

// Mirrors the renderer's planner: with the field waiting for a consent, every
// script, link or style that names no category of its own stays out for a
// visitor who rejects. So does one whose category the banner does not offer,
// since nobody can ever accept it. Meta tags are never held back.
export function blockedCustomCodeElements (code: string | null | undefined, mode: string, bannerConfiguration: Record<string, any> | null | undefined): number {
  if (!code || mode === CUSTOM_CODE_ALWAYS) {
    return 0
  }

  const parsed = new DOMParser().parseFromString(code, 'text/html')
  const own = [CUSTOM_CODE_NECESSARY, ...visibleCategories(bannerConfiguration)] as string[]

  return ['script', 'link', 'style']
    .flatMap(tag => Array.from(parsed.getElementsByTagName(tag)))
    .filter(element => !own.includes(element.getAttribute('data-category') || ''))
    .length
}

// True when something on the site already links to the cookie settings, so a
// tenant who switched the icon off still gives visitors a way back.
export function draftLinksToCookieSettings (draft: Record<string, any> | null | undefined): boolean {
  if (!draft) {
    return false
  }

  return JSON.stringify({ navbar: draft.navbar, footer: draft.footer, pages: draft.pages }).includes(COOKIE_SETTINGS_HASH)
}
